import React, { useContext, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Spinner, Tab } from 'react-bootstrap'
import { Tabs } from '../../../components/Tabs/Tabs'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetOAuth2AppByClientIdQuery } from '../../../store/auth/auth'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { IntegrationManagementHeader } from '../../Integration/IntegrationManagement/IntegrationManagementHeader'
import { ManageAppDetailsTab } from './ManageAppDetailsTab'
import { ManageAppRedirectUrisTab } from './ManageAppRedirectUrisTab'
import { ManageAppScopesTab } from './ManageAppScopesTab'
import { ManageAppCredentialsTab } from './ManageAppCredentialsTab'
import styles from './ManageOAuth2App.module.scss'
import './ManageOAuth2App.scss'

const DEFAULT_TAB = 'details'

const TabTitle = ({ title }: { title: string }) => <div className='d-flex align-items-center'>{title}</div>

export const ManageOAuth2App: React.FC = () => {
  const { appId, manageTab = DEFAULT_TAB } = useParams<{ appId: string; manageTab?: string }>() as { appId: string; manageTab?: string }
  const accountId = useAppSelector(selectAccountId)
  const navigate = useNavigate()
  const { portalRef } = useContext(HeaderContext)
  const { data: oAuth2App, isLoading } = useGetOAuth2AppByClientIdQuery({ accountId, clientId: appId })

  useDocumentTitle(oAuth2App ? `Manage ${oAuth2App.name}` : 'Manage App')

  const handleTabSelect = (key: string | null) => {
    if (key && key !== manageTab) {
      navigate(`/account/integration-settings/oauth2apps/${appId}/manage/${key}`, { replace: true })
    }
  }

  const [headerContainer, setHeaderContainer] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (portalRef && portalRef.current) {
      setHeaderContainer(portalRef.current)
    }
  }, [portalRef])

  if (isLoading) {
    return (
      <div className='d-flex align-items-center justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (!oAuth2App) {
    return <Navigate to='/account/integration-settings/oauth2apps' replace />
  }

  return (
    <div className={styles['manage-container']}>
      {headerContainer
        ? createPortal(
            <IntegrationManagementHeader
              backTarget='app'
              backPathOverride={`/account/integration-settings/oauth2apps/${appId}`}
              title={<span className='fw-semibold'>Manage &lsquo;{oAuth2App.name}&rsquo; app</span>}
            />,
            headerContainer,
          )
        : null}

      <div className='d-flex gap-5 oauth2-manage-tabs'>
        <Tabs onSelect={handleTabSelect} activeKey={manageTab} id='manage-app-tabs' className='vertical-tab'>
          <Tab className='mt-2' eventKey='details' title={<TabTitle title='App details' />}>
            <ManageAppDetailsTab oAuth2App={oAuth2App} />
          </Tab>

          <Tab className='mt-2' eventKey='redirect-uris' title={<TabTitle title='Redirect URIs' />}>
            <ManageAppRedirectUrisTab oAuth2App={oAuth2App} />
          </Tab>

          <Tab className='mt-2' eventKey='scopes' title={<TabTitle title='Scopes' />}>
            <ManageAppScopesTab oAuth2App={oAuth2App} />
          </Tab>

          <Tab className='mt-2' eventKey='credentials' title={<TabTitle title='Credentials' />}>
            <ManageAppCredentialsTab oAuth2App={oAuth2App} />
          </Tab>
        </Tabs>
      </div>
    </div>
  )
}
